import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { ParentScreenCtx } from "../../../../pages/workbench_build/WorkbenchBuild";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useContext, useState, useEffect, useCallback } from "react";
import { api } from "../../../../redux/api";
import { cloneDeep, debounce, isArray, isEqual } from "lodash";
import { eachDeep } from "deepdash-es/standalone";
import { debounceFunction } from "../../../../utils/utilFunctions";
import DefaultCheckbox from "./DefaultCheckbox";
import { treeActions } from "../../../../redux/slices/treeSlice";
import { Spinner } from "../../../Repository/DataDepot/DataDepot";

export const DatabaseColumn = ({ initialColumn, initialKey, initialValid, updatedSelectedNode, setUpdatedSelectedNode}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { account, user } = useSelector(state => state.queryParams);
  const parentScreen = useContext(ParentScreenCtx);
  const { rightTree } = useSelector((state) => state.treeData[parentScreen]);
  const selectedNode = rightTree.tree[rightTree.currentTab].selectedNode;
  const translatedData = rightTree.tree[rightTree.currentTab].translatedData;
  
  // parent node of current selected node
  const parentNode = getParentRecord(updatedSelectedNode, translatedData);

  //if parent db record statement type is UPDATE, or if it is INSERT and has ON_STATEMENT_ERROR_FLIP checkbox true.
  const keyCheckboxShown = 
    (["TRUE", "true", true].includes(parentNode?.ON_STATEMENT_ERROR_FLIP) && parentNode?.STATEMENT === 'INSERT') ||
    (parentNode?.STATEMENT === "UPDATE");

  //if parent db record statement type is UPDATE, or if it is INSERT and has UNIQUE checkbox true.
  const validCheckboxShown =
    (["TRUE", "true", true].includes(parentNode.UNIQUE) && parentNode?.STATEMENT === "INSERT") || 
    (parentNode?.STATEMENT && parentNode.STATEMENT === "UPDATE");

  //control value states
  const [column, setColumn] = useState(updatedSelectedNode?.COLUMN_NAME || t('SELECT'));                                         // "Database Column" freeSolo autocomplete
  const [keyCheckboxValue, setKeyCheckboxValue] = useState(updatedSelectedNode?.KEY === "TRUE" ? true : false);         // "Is Database Key" checkbox
  const [validCheckboxValue, setValidCheckboxValue] = useState(updatedSelectedNode?.UNIQUE === "TRUE" ? true : false);  // "Verify Field Value Changed" checkbox

  const modifyUpdatedNode = (prop, val) => {
    if (val !== "" && val !== undefined) {
      setUpdatedSelectedNode(prev => {
        return {...prev, [prop]: val}
      });
    } else {
      setUpdatedSelectedNode(prev => {
        return {...prev, [prop]: null}
      });
    }
  }

  const handleColumnChange = (colName) => {
    if (colName === null) setColumn(t('SELECT'));
    else setColumn(colName || "");

    let sudoVal = [t('SELECT'), null, false, undefined].includes(colName) ? '' : colName;
    modifyUpdatedNode("COLUMN_NAME", sudoVal);
  }

  const handleKeyChange = (val) => {
    setKeyCheckboxValue(val);
    modifyUpdatedNode("KEY", val ? "TRUE" : "")
  }

  const handleValidChange = (val) => {
    setValidCheckboxValue(val);
    modifyUpdatedNode("UNIQUE", val ? "TRUE" : "");
  }

  //options for "Database Column" dropdown, defined on each render.
  const columnOptions = 
    column && parentNode?.db_table_columns ? [t('SELECT'), ...parentNode.db_table_columns, column]  //selectedNode has COLUMN_NAME, and parent has DB_TABLE_COLUMNS
    : !column && parentNode?.db_table_columns ? [t('SELECT'), ...parentNode.db_table_columns]       //selectedNode has no COLUMN_NAME, but parent has DB_TABLE_COLUMNS
    : column && !parentNode?.db_table_columns ? [t('SELECT'), column]                               //selectedNode has COLUMN_NAME, but parent has no DB_TABLE_COLUMNS
    : [t('SELECT')];                                                                           //selectedNode has no COLUMN_NAME, and parent has no DB_TABLE_COLUMNS

  return (
    <>
      <Autocomplete
        freeSolo
        size="small"
        variant="outlined"
        className='width-100 margin-right-8 margin-top-12'
        // disabled={!ddLoaded || !colOptionsDisplay.includes(t('SELECT'))}
        options={[...new Set(columnOptions)]}
        value={column || ''}
        inputValue={column}
        onChange={(event, newValue) => {
          handleColumnChange(newValue);
        }}
        renderInput={(params) => <TextField 
          size="small" 
          variant="outlined"
          {...params} 
          label={t("LBL_COL_NM")}
          value={column || ''}
          onChange={(e) => {
            handleColumnChange(e?.target?.value || "");
          }}
          // InputProps={{
          //   ...params.InputProps,
          //   endAdornment: !ddLoaded || !colOptionsDisplay.includes(t('SELECT')) ? <CircularProgress size={"14px"} /> : params.InputProps.endAdornment
          // }}
        />}
      />
      <div className="flex width-100" style={{overflow: "hidden"}}>
        { // "is Database Key" checkbox - shown if parent record has prop ON_STATEMENT_ERROR_FLIP = "TRUE"
        (keyCheckboxShown) && (
          <DefaultCheckbox 
            labelSx={{minWidth: "fit-content"}}
            label={t("LBL_DATABASE_KEY")}
            name={"LBL_DATABASE_KEY"}
            value={keyCheckboxValue}
            setValue={(val) => {
              handleKeyChange(val)
            }}
          />
        )}
        { // "Verify Field Value Changed" checkbox - shown if parent record has prop UNIQUE = "TRUE"
        (validCheckboxShown) && (
          <DefaultCheckbox 
            labelSx={{minWidth: "fit-content"}}
            label={t("LBL_DATABASE_UNIQUE")}
            name={"LBL_DATABASE_UNIQUE"}
            value={validCheckboxValue}
            setValue={(val) => {
              handleValidChange(val)
            }}
          />
        )}
      </div>
    </>

  )
}


//UTIL

//return the parent node of a given selectedNode.
export const getParentRecord = (sNode, tData) => {
  let parNode = false;
  let updatedData = eachDeep(tData, (value, key, parentArr, ctx) => {
    if (typeof value !== 'object' || parNode !== false) return;
    if (value.children) {
      value.children.forEach((o, i) => {
        if (typeof value !== 'object' || parNode !== false) return;
        if (o?.nodeId && o.nodeId === sNode.nodeId) {
          parNode = value;
        }
      })
    }
  });

  return parNode;
}